import { Skeleton } from "@gigpro/ui/Skeleton";
import { cn } from "@gigpro/ui/tailwind/cn";
import { useQuery } from "@tanstack/react-query";
import { WagePicker, type WagePickerProps } from "domains/postGig/components/WagePicker";
import { gigTemplatesQuery } from "domains/postGig/containers/PositionSelect/queries";
import { skillsQuery } from "domains/postGig/containers/WagePicker/queries";
import type { SectionPros } from "domains/postGig/providers/PostGigData/types";
import { useFeatureFlag } from "providers/LaunchDarkly";

export interface WagePickerContainerProps extends Omit<
  WagePickerProps,
  "avgWage" | "recommendedWage" | "templateWage" | "value" | "onValueChange" | "recommendationRefresh"
> {
  value: SectionPros[number]["wageOverride"];
  onValueChange: (value: NonNullable<SectionPros[number]["wageOverride"]>) => void;
  template?: {
    id?: number;
    locationId?: number;
    wage: number;
  };
  skillId: number;
  /** In place of the skill's: last time's wage, for a role from the location's own positions. */
  recommendedWage?: number;
}

export function WagePickerContainer({
  skillId,
  template,
  className,
  onValueChange,
  recommendedWage,
  ...props
}: Readonly<WagePickerContainerProps>) {
  const recommendationRefresh = useFeatureFlag("wage-recommendation-refresh");
  const skills = useQuery(skillsQuery());
  const templates = useQuery({
    ...gigTemplatesQuery({ location: { id: template?.locationId ?? -1 } }),
    enabled: !!template?.locationId,
  });
  const selectedSkill = skills.data?.find(({ id }) => id === skillId);

  if (!selectedSkill) return <Skeleton className={cn("h-14.5 w-full scale-y-60 self-center", className)} />;

  // A custom wage never writes back silently: single-shift asks on Continue, multi-shift keeps it per shift.
  const onChange: WagePickerProps["onValueChange"] = (value) => {
    onValueChange({ ...value, skill: selectedSkill });
  };
  return (
    <WagePicker
      key={skillId}
      className={className}
      onValueChange={onChange}
      {...props}
      recommendationRefresh={recommendationRefresh}
      templateWage={template?.wage}
      // Only a saved position's own wage is "saved": on the posting page `template.wage` is the role's current pay.
      savedWage={templates.data?.find(({ id }) => id === template?.id)?.hourly_rate ?? null}
      recommendedWage={recommendedWage ?? selectedSkill.recommended_wage_amount ?? 0}
      avgWage={selectedSkill.average_wage_amount ?? 0}
    />
  );
}
